import React, { useCallback, useEffect, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import NoDataPage from '../../../Components/NoDataPage';
import PageHeading from '../../../Components/PageHeading';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { Body, SubHeading } from '../../../Constants/Fonts';
import { risk_portal_list_scenarios, risk_portal_delete_scenario } from '../../../services';
import ScenarioForm from '../Forms/ScenarioForm';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// A single metric chip: muted label + emphasised value.
function Metric({ label, value }) {
    return (
        <Body as="span" variant={2} color="var(--color-text-secondary)">
            {label}: <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">{value}</Body>
        </Body>
    );
}

function ScenarioCard({ scenario, canManage, onDelete }) {
    const s = scenario;
    return (
        <div className="rp1_scenario_card">
            <div className="rp1_scenario_card_head">
                <SubHeading variant={3} as="span" color="var(--color-text-primary)" style={{ minWidth: 0 }}>{s.name}</SubHeading>
                {canManage && (
                    <CommonActionMenu actions={[{ label: 'Delete', onClick: () => onDelete(s), danger: true }]} />
                )}
            </div>
            {s.description && (
                <Body variant={2} color="var(--color-text-secondary)" style={{ margin: 0 }}>{s.description}</Body>
            )}
            <div className="rp1_scenario_metrics">
                <Metric label="Likelihood" value={`${s.likelihood}/5`} />
                {s.impact_financial != null && s.impact_financial !== '' && (
                    <Metric label="Financial" value={`$${Number(s.impact_financial).toLocaleString()}`} />
                )}
                <Metric label="Operational" value={`${s.impact_operational}/5`} />
                <Metric label="Reputational" value={`${s.impact_reputational}/5`} />
                <Metric label="Regulatory" value={`${s.impact_regulatory}/5`} />
                <Metric label="Linked Risks" value={s.linked_risks_count ?? 0} />
            </div>
        </div>
    );
}

// Scenarios — "what-if" scenario & stress testing. List of scenario cards with
// create + delete. Data from risk_portal_list_scenarios.
export default function Scenarios({ canManage = false }) {
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const load = useCallback(async () => {
        try {
            const d = unwrap(await risk_portal_list_scenarios());
            setScenarios(Array.isArray(d) ? d : (Array.isArray(d?.scenarios) ? d.scenarios : []));
        } catch (e) {
            notify('Failed to load scenarios', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const remove = async (s) => {
        const ok = await confirm({
            title: 'Delete scenario',
            message: `Delete "${s.name}"? This can't be undone.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await risk_portal_delete_scenario(s.id);
            notify(`Scenario "${s.name}" deleted`, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Failed to delete scenario', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <CommonLoader fullPage label="Loading scenarios…" />;

    return (
        <div className="rp1_scenarios">
            <PageHeading
                title="Scenario & Stress Testing"
                subtitle='Model "what-if" scenarios for emerging risks and their aggregate impact.'
                actions={canManage ? <CommonButton text="+ New Scenario" onClick={() => setShowForm(true)} /> : null}
            />

            {scenarios.length === 0 ? (
                <NoDataPage
                    title="No scenarios yet"
                    hint='Create a "what-if" scenario to model emerging risks and stress-test your posture.'
                />
            ) : (
                <div className="rp1_scenario_grid">
                    <div className='rp1_widget'>
                        {scenarios.map((s) => (
                            <ScenarioCard key={s.id} scenario={s} canManage={canManage} onDelete={remove} />
                        ))}
                    </div>
                </div>
            )}

            <ScenarioForm
                show={showForm}
                onClose={() => setShowForm(false)}
                onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                onError={(msg) => notify(msg, 'error')}
            />

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
